// Rounds: one outing logged by a distributor (or from Farm OS for them) with
// its proof pictures. RoundCard shows one and, while it's waiting, the
// controls to approve it (with the pay) or send it back with a message. The
// To check tab lists everything waiting, oldest first.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Textarea } from '../lib/ui';
import { formatDay, formatGBP } from '../lib/trade';
import {
  payFor, jobProgress, perHour, perKm, formatCount, formatMinutes, roundsToCheck,
} from '../lib/leaflets';
import { byId, PersonTag, RunPill, Photos } from './LeafletsCommon';
import { Label, errorText } from './TradeCommon';

// Things worth a second look before paying.
function checks(job, run, zone) {
  const out = [];
  const { approved, waiting } = jobProgress(job);
  if (approved + waiting > job.leaflets) {
    out.push(`With this round the job adds up to ${formatCount(approved + waiting)}, more than the ${formatCount(job.leaflets)} leaflets given.`);
  }
  if (zone?.homes && approved + waiting > zone.homes * 1.15) {
    out.push(`With this round, ${formatCount(approved + waiting)} leaflets have gone into ${zone.name}, which has about ${formatCount(zone.homes)} homes.`);
  }
  if (!run.photos?.length) out.push('No pictures with this round.');
  return out;
}

export function RoundCard({ job, run, zone, person, onJob, flash, showWho = true }) {
  const waiting = run.status === 'submitted';
  const [pay, setPay] = useState(() => String(payFor(run.delivered, job.ratePer1000).toFixed(2)));
  const [delivered, setDelivered] = useState(String(run.delivered));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const act = async (action, body = {}) => {
    setBusy(action);
    setError('');
    try {
      const { data } = await api.post(`/leaflets/admin/jobs/${job._id}/runs/${run._id}/review`, { action, ...body });
      onJob(data.job);
      if (action === 'approve') flash(`Approved: ${formatGBP(data.job.runs.find(r => r._id === run._id)?.pay)} to pay ${person?.name || ''}`.trim());
      if (action === 'reject') flash('Sent back with your message', 'info');
      if (action === 'reopen') flash('Back to waiting to be checked', 'info');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  };

  const approve = () => act('approve', { pay, note, ...(Number(delivered) !== run.delivered ? { delivered } : {}) });
  const reject = () => {
    if (!note.trim()) return setError('Say what\'s wrong, so they know what to send instead');
    return act('reject', { note });
  };
  const remove = async () => {
    if (!window.confirm('Delete this round and its pictures? This can\'t be undone.')) return;
    try {
      const { data } = await api.delete(`/leaflets/admin/jobs/${job._id}/runs/${run._id}`);
      onJob(data.job);
      flash('Round deleted', 'info');
    } catch (e) {
      setError(errorText(e));
    }
  };

  const speed = [
    run.distanceKm ? `${run.distanceKm} km` : null,
    run.minutes ? formatMinutes(run.minutes) : null,
    perHour(run) ? `${formatCount(perHour(run))} an hour` : null,
    perKm(run) ? `${formatCount(perKm(run))} per km` : null,
  ].filter(Boolean);
  const warnings = waiting ? checks(job, run, zone) : [];
  const deliveredNow = Number(delivered) || 0;
  const payNow = Number(String(pay).replace(/[£,\s]/g, ''));

  return (
    <article className={`lf-round${waiting ? ' is-waiting' : ''}`}>
      <header className="lf-round__head">
        <div className="lf-round__when">
          <b>{formatDay(run.date, { weekday: true })}</b>
          {showWho && <> <PersonTag person={person} /> <span className="lf-muted">in {zone?.name || 'a zone'}</span></>}
        </div>
        <RunPill run={run} />
      </header>

      <div className="lf-round__body">
        <div className="lf-round__facts">
          <div className="lf-round__count">{formatCount(run.delivered)} <small>leaflets</small></div>
          {speed.length > 0 && <div className="lf-muted">{speed.join(' · ')}</div>}
          {run.finished && <div className="lf-flag">Says the zone is finished, or they ran out</div>}
          {run.loggedBy === 'admin' && <div className="lf-muted">Logged from Farm OS</div>}
          {run.notes && <blockquote className="lf-quote">{run.notes}</blockquote>}
          {run.status === 'approved' && (
            <div className="lf-round__pay">
              {formatGBP(run.pay)} {run.payout ? 'paid' : 'to pay'}
              {run.reviewNote && <span className="lf-muted"> &middot; you said: {run.reviewNote}</span>}
            </div>
          )}
          {run.status === 'rejected' && <div className="lf-round__sent">You said: {run.reviewNote}</div>}
        </div>
        <Photos photos={run.photos} />
      </div>

      {warnings.length > 0 && (
        <ul className="lf-warnings">{warnings.map(w => <li key={w}>{w}</li>)}</ul>
      )}

      {waiting && (
        <div className="lf-review">
          <div className="lf-review__fields">
            <div>
              <Label hint="if the screenshot says otherwise">Leaflets</Label>
              <Input type="number" min="1" value={delivered} onChange={e => {
                setDelivered(e.target.value);
                setPay(String(payFor(Number(e.target.value) || 0, job.ratePer1000).toFixed(2)));
              }} style={{ width: 110 }} />
            </div>
            <div>
              <Label hint={`£${job.ratePer1000} per 1000`}>Pay</Label>
              <div className="pl-price"><Input value={pay} onChange={e => setPay(e.target.value)} style={{ width: 110 }} /></div>
            </div>
            <div className="lf-review__note">
              <Label hint="they see this in the app">Message</Label>
              <Textarea rows={1} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional when approving. Needed when sending back." />
            </div>
          </div>
          {error && <div className="ui-banner ui-banner--bad">{error}</div>}
          <div className="lf-row">
            <Button variant="primary" onClick={approve} disabled={!!busy || deliveredNow < 1}>
              {busy === 'approve' ? 'Approving…' : `Approve, ${formatGBP(Number.isFinite(payNow) ? payNow : 0)}`}
            </Button>
            <Button variant="danger" onClick={reject} disabled={!!busy}>{busy === 'reject' ? 'Sending…' : 'Send back'}</Button>
            <Button variant="muted" size="sm" onClick={remove} disabled={!!busy} style={{ marginLeft: 'auto' }}>Delete</Button>
          </div>
        </div>
      )}

      {!waiting && !run.payout && (
        <div className="lf-row lf-round__after">
          {error && <div className="ui-banner ui-banner--bad">{error}</div>}
          <Button size="sm" variant="muted" onClick={() => act('reopen')} disabled={!!busy}>
            {run.status === 'approved' ? 'Undo approval' : 'Look at it again'}
          </Button>
          <Button size="sm" variant="muted" onClick={remove} disabled={!!busy}>Delete</Button>
        </div>
      )}
    </article>
  );
}

export default function LeafletsReviewTab({ data, putJob, flash }) {
  const people = byId(data.distributors);
  const zones = byId(data.zones);
  const waiting = roundsToCheck(data.jobs);
  const recent = data.jobs
    .flatMap(job => job.runs.filter(r => r.status !== 'submitted' && r.reviewedAt).map(run => ({ job, run })))
    .sort((a, b) => String(b.run.reviewedAt).localeCompare(String(a.run.reviewedAt)))
    .slice(0, 6);

  return (
    <div className="lf-rounds">
      {waiting.length ? waiting.map(({ job, run }) => (
        <RoundCard key={run._id} job={job} run={run} zone={zones[job.zone]} person={people[job.distributor]} onJob={putJob} flash={flash} />
      )) : (
        <div className="trade-empty">
          <strong>Nothing to check</strong>
          Rounds sent in from the leaflets app turn up here, with their screenshots.
        </div>
      )}
      {recent.length > 0 && (
        <>
          <h3 className="lf-subhead">Checked lately</h3>
          {recent.map(({ job, run }) => (
            <RoundCard key={run._id} job={job} run={run} zone={zones[job.zone]} person={people[job.distributor]} onJob={putJob} flash={flash} />
          ))}
        </>
      )}
    </div>
  );
}
